// clamps string
function clamp(text: string, coeff: number, splitter = '', reverse = false) {
  const textChunks = String(text).split(splitter);
  const sliceIndx = Math.floor(textChunks.length * coeff);
  if (sliceIndx < 1) {
    return '';
  }

  const clampedTextChunks = reverse ? textChunks.slice(-sliceIndx) : textChunks.slice(0, sliceIndx);

  return clampedTextChunks.join(splitter);
}

// returns copy of the DOM-element you are trying to clamp with the matching styles.
function createSimilarEl(sample: Element, styles = {}) {
  const element = document.createElement(sample.tagName);
  const sampleStyles = window.getComputedStyle(sample);
  const sampleStylesText = sampleStyles.cssText;

  element.style.cssText = sampleStylesText;
  element.style.maxWidth = sampleStyles.width;

  const styleKeys = Object.keys(styles);
  styleKeys.forEach((property) => {
    element.style[property] = styles[property];
  });
  return element;
}

// Adds ellipsis & prefix to string
function constructString(str = '', ellipsis = '', reverse = false) {
  if (str === '') {
    return '';
  }
  return reverse ? `${ellipsis}${str}` : `${str}${ellipsis}`;
}

/**
   * 
   returns clamped string for a DOM-element
   
   text: Text to be clamped
   element: element in which text will be put into
   lines: amount of lines you which to clamp at
   ellipsis: the trailing characters you wish to add (Ex. ...)
   splitter: optional. Use " " if you wish to remove entire words instead of splitting them
   reverse: Allows the option to reverse the direction of the string if necessary
  
   The function essentially creates a duplicate of your element, inserts the desired text, 
   and checks that the number of lines is equal to or less than the number desired. 
   If it goes over, it removes characters until the desired number of lines is reached.
   This function should work best with element that have a max-width property but may work
   with element that do not have it.
  
   *  */

export const clampLines = ({
  text,
  element,
  lines,
  ellipsis,
  splitter,
  reverse,
}: {
  text: string;
  element: Element;
  lines: number;
  ellipsis: string;
  splitter?: string;
  reverse?: boolean;
}): { modified: boolean; text: string } => {
  // accounts for very large font;
  const maxHeight = 200 * Number(lines);
  const testEl = createSimilarEl(element, {
    lineHeight: `${200}px`,
    height: 'auto',
    position: 'absolute',
    opacity: '0',
    left: '-1px',
    width: `${element!.scrollWidth * 1}px`,
    paddingTop: 0,
    paddingBottom: 0,
  });

  element!.appendChild(testEl);
  let clampedText = text;
  //  set the test element innerText to the constructed string with ellipsis
  testEl.innerText = constructString(clampedText, ellipsis, reverse);
  let testElHeight = Math.ceil(testEl.scrollHeight) - 1;

  //  checks to see if the element height is bigger than the allowed height
  if (testElHeight <= maxHeight) {
    testEl.parentNode!.removeChild(testEl);
    //  returns same text as given if the number of lines is lower or equal to the limit
    return { modified: false, text: clampedText };
  }

  let decrementCoeff = maxHeight / testElHeight + 0.35;
  // essentially removes letter until below the allowed height
  while (testElHeight > maxHeight && clampedText.length) {
    // clamps text. decrementCoeff is the amount of letters to be removed;
    clampedText = clamp(text, decrementCoeff, splitter, reverse);
    // resets innerHTML
    testEl.innerText = constructString(clampedText, ellipsis, reverse);
    // resets height
    testElHeight = Math.ceil(testEl.scrollHeight) - 1;
    decrementCoeff -= text.length > 1500 ? 0.011 : 0.018;
  }
  testEl.parentNode!.removeChild(testEl);
  return { modified: true, text: constructString(clampedText, ellipsis, reverse) };
};
